import { ApplicationRef, Injectable, Type } from '@angular/core';
import { BaseModalComponent } from 'src/ui/components/base-modal/base-modal.component';

@Injectable({ providedIn: 'root' })
export class BaseModalService {
  private readonly modals: BaseModalComponent[] = [];
  private currentZIndex = 50;

  constructor(private readonly appRef: ApplicationRef) {}

  register(modal: BaseModalComponent) {
    this.modals.push(modal);
  }

  unregister(modal: BaseModalComponent) {
    const index = this.modals.indexOf(modal);
    if (index > -1) {
      this.modals.splice(index, 1);
    }
  }

  async open<T>(
    component: Type<T>,
    options?: {
      title?: string;
      inputs?: Partial<T>;
      showHeader?: boolean;
      showFooter?: boolean;
      confirmButtonText?: string;
      cancelButtonText?: string;
      showConfirmButton?: boolean;
      showCancelButton?: boolean;
      cancelButtonType?: string;
      width?: string;
      customClass?: string;
      confirmButtonType?: string;
      asyncConfirm?: boolean;
      letEvents?: boolean;
      confirmButtonDisabled?: boolean;
    }
  ): Promise<any> {
    let modal = this.modals.find((m) => !m.visible);

    if (!modal) {
      const rootElement = this.appRef.components[0].location.nativeElement;
      const modalElement = document.createElement('app-base-modal');
      rootElement.appendChild(modalElement);

      modal = this.modals.at(-1);
    }

    if (!modal) throw new Error('Base modal no registrado');

    modal.title = options?.title ?? '';
    modal.showHeader = options?.showHeader ?? true;
    modal.showFooter = options?.showFooter ?? true;
    modal.confirmButtonText = options?.confirmButtonText ?? 'Continuar';
    modal.cancelButtonText = options?.cancelButtonText ?? 'Cancelar';
    modal.confirmButtonDisabled = options?.confirmButtonDisabled ?? false;
    modal.letEvents = options?.letEvents ?? false;
    modal.width = options?.width ?? '560px';
    modal.customClass = options?.customClass ?? '';
    modal.confirmButtonType = options?.confirmButtonType ?? 'primary';
    modal.cancelButtonType = options?.cancelButtonType ?? 'dark-borderless';
    modal.zIndex = this.currentZIndex;
    modal.showCancelButton = options?.showCancelButton ?? true;
    modal.showConfirmButton = options?.showConfirmButton ?? true;
    this.currentZIndex += 10;

    modal.open();

    let container = modal.contentContainer;
    let attempts = 0;
    const maxAttempts = 10;

    while (!container && attempts < maxAttempts) {
      await new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 50);
      });
      container = modal.contentContainer;
      attempts++;
    }

    if (!container) {
      throw new Error(
        'Container no disponible después de múltiples intentos. Asegúrate de que el modal está visible.'
      );
    }

    container.clear();

    const contentRef = container.createComponent(component);
    if (options?.inputs) {
      Object.assign(contentRef.instance as object, options.inputs);
    }

    if ('confirmModal' in (contentRef.instance as any)) {
      (contentRef.instance as any).confirmModal = () => modal.confirm();
    }

    if ('closeModal' in (contentRef.instance as any)) {
      (contentRef.instance as any).closeModal = () => modal.close();
    }

    if (
      typeof (contentRef.instance as any).setConfirmButtonControl === 'function'
    ) {
      (contentRef.instance as any).setConfirmButtonControl(
        (disabled: boolean) => {
          modal.confirmButtonDisabled = disabled;
          modal.cdr.detectChanges();
        }
      );
    }

    if (
      typeof (contentRef.instance as any).setConfirmButtonVisibilityControl ===
      'function'
    ) {
      (contentRef.instance as any).setConfirmButtonVisibilityControl(
        (show: boolean) => {
          modal.showConfirmButton = show;
          modal.cdr.detectChanges();
        }
      );
    }

    if (
      typeof (contentRef.instance as any).setCancelButtonControl === 'function'
    ) {
      (contentRef.instance as any).setCancelButtonControl((show: boolean) => {
        modal.showCancelButton = show;
        modal.cdr.detectChanges();
      });
    }

    contentRef.changeDetectorRef.detectChanges();

    return new Promise((resolve) => {
      modal.confirmFn = async () => {
        const result =
          typeof (contentRef.instance as any).getData === 'function'
            ? await (contentRef.instance as any).getData()
            : contentRef.instance;

        if (result !== null && result !== undefined) {
          if (
            options?.asyncConfirm &&
            typeof (contentRef.instance as any).onConfirm === 'function'
          ) {
            const shouldClose = await (contentRef.instance as any).onConfirm(
              result
            );
            if (shouldClose) {
              modal.visible = false;
              this.currentZIndex -= 10;
              resolve(result);
            }
          } else {
            modal.visible = false;
            this.currentZIndex -= 10;
            resolve(result);
          }
        }
      };

      modal.closeFn = () => {
        modal.visible = false;
        this.currentZIndex -= 10;
        resolve(null);
      };
    });
  }

  close() {
    const reversedModals = [...this.modals].reverse();
    const lastOpenModal = reversedModals.find((m) => m.visible);
    lastOpenModal?.close();
  }

  confirm() {
    const reversedModals = [...this.modals].reverse();
    const lastOpenModal = reversedModals.find((m) => m.visible);
    lastOpenModal?.confirm();
  }

  closeAll() {
    for (const m of this.modals) {
      m.close();
    }
    this.currentZIndex = 50;
  }
}
